import React, { useState } from 'react';
import { QuestionStepDefinition, SurveyDefinition, SurveyStepDefinition } from '../../../types/Survey';
import { ManagedResultsLookup } from '../../../helpers/surveyPredicates';
import { ResultModel, StepResultModel } from '../../../types/SurveyResult';
import { getFormStyle, getIconImageStyles, getTextStyles } from '../Util/surveyStyles';
import { canAutoContinue, buildStepResult, resolvePresentedDate, clearResultForSkip } from '../Util/surveyStepUtils';
import { getDefaultSurveyQuestionResult } from '../Util/getDefaultSurveyQuestionResult';
import { isFieldsetFormat } from '../../../helpers/answerFormatHelper';
import { hasAnswer } from '../../../helpers/surveyResultHelper';
import IconImage from '../Components/IconImage/IconImage';
import SurveyMarkdown from '../Components/SurveyMarkdown/SurveyMarkdown';
import SurveyQuestion from '../Components/SurveyQuestion/SurveyQuestion';
import SurveyStepStickyFooter from '../Components/SurveyStepStickyFooter/SurveyStepStickyFooter';

export interface QuestionStepProps {
    step: QuestionStepDefinition;
    surveyDefinition: SurveyDefinition;
    nextStep: SurveyStepDefinition | null;
    managedResults: ManagedResultsLookup;
    predicateExpressionEvaluator: (expression: string) => boolean;
    onContinue: (stepResult: StepResultModel) => void;
    onBack: () => void;
    onResultChange: (stepResult: StepResultModel) => void;
    presentedDate?: string;
}

export default function QuestionStep({
    step,
    surveyDefinition,
    nextStep,
    managedResults,
    onContinue,
    onResultChange: onResultChangeProp,
    presentedDate: presentedDateProp,
}: QuestionStepProps) {
    const presentedDate = resolvePresentedDate(presentedDateProp);
    const last = nextStep == null;
    const isFieldset = isFieldsetFormat(step.answerFormat);
    const inputId = (step.identifier ?? '').replace(/[^a-zA-Z0-9_-]/g, '_');

    const stepResult = managedResults[step.identifier ?? ''];
    const hadPreviousAnswer = stepResult !== undefined;
    const auto = canAutoContinue(step.answerFormat, hadPreviousAnswer);

    const previousQuestionResult = stepResult?.QuestionResults?.find(
        r => r.Identifier === step.identifier
    );
    const [result, setResult] = useState<ResultModel>(() => {
        const r = previousQuestionResult ?? getDefaultSurveyQuestionResult(step.identifier ?? '', step.answerFormat);
        return { ...r, StartDate: r.StartDate ?? presentedDate };
    });

    const nextDisabled = !hasAnswer(result) || !!result.invalid;

    function handleResultChange(newResult: ResultModel) {
        setResult(newResult);
        onResultChangeProp(buildStepResult(step.identifier ?? '', presentedDate, [newResult]));
        if (auto && hasAnswer(newResult) && !newResult.invalid) {
            onContinue(buildStepResult(step.identifier ?? '', presentedDate, [newResult]));
        }
    }

    function handleContinue() {
        if (nextDisabled) return;
        onContinue(buildStepResult(step.identifier ?? '', presentedDate, [result]));
    }

    function handleSkip() {
        onContinue(buildStepResult(step.identifier ?? '', presentedDate, [clearResultForSkip(result)]));
    }

    const labelContent = (
        <>
            {step.title && (
                <div className="step-title" style={getTextStyles('title', surveyDefinition, step, true)}>
                    <SurveyMarkdown text={step.title} inline={true} />
                </div>
            )}
            {step.text && (
                <div className="step-text" style={getTextStyles('text', surveyDefinition, step, true)}>
                    <SurveyMarkdown text={step.text} />
                </div>
            )}
            {step.image && (
                <div className="step-image-main">
                    <img src={step.image} alt={step.imageAltText} />
                </div>
            )}
        </>
    );

    const questionEl = (
        <div className="section">
            <SurveyQuestion
                answerFormat={step.answerFormat}
                result={result}
                onResultChange={handleResultChange}
                onEnterPressed={handleContinue}
                optional={step.optional}
                placeholder={step.placeholder}
                variant={getFormStyle(surveyDefinition, step)}
                inputIdForLabel={inputId}
                useFieldsetFormat={isFieldset}
                autoContinue={auto ? handleContinue : undefined}
            />
        </div>
    );

    return (
        <>
            <div className="current-step">
                <IconImage iconImage={step.iconImage} iconImageStyles={getIconImageStyles(surveyDefinition, step)} />
                {isFieldset ? (
                    <fieldset className="step-content">
                        <legend>{labelContent}</legend>
                        {questionEl}
                    </fieldset>
                ) : (
                    <div className="step-content">
                        <label htmlFor={inputId}>{labelContent}</label>
                        {questionEl}
                    </div>
                )}
            </div>
            <SurveyStepStickyFooter
                surveyDefinition={surveyDefinition}
                step={step}
                showNextButton={!auto}
                nextButtonText={step.nextButtonText}
                isLastStep={last}
                onContinue={handleContinue}
                nextDisabled={nextDisabled}
                canSkip={step.optional !== false}
                onSkip={handleSkip}
            />
        </>
    );
}

